import { useNavigate } from '@tanstack/react-router'
import { MapPin } from 'lucide-react'
import type { CategoryStyle } from '@/lib/types'
import { Badge, Card, Row } from '@/components/ui'
import { toneOfLabel } from '@/lib/types'
import { entitySlug, stripMarkdown } from '@/lib/helpers'

interface EntityKindLike {
  id: number
  name: string
  emoji: string
}

interface EntityLike {
  id: number
  name: string
  description: string
  kind_id: number
  tags?: Array<string> | null
  image_uri?: string
  link?: string
  location_text?: string
}

interface EntityCardProps {
  entity: EntityLike
  kinds: Array<EntityKindLike>
  /** Render as a non-clickable live preview (used in the entity form). */
  preview?: boolean
  /** External link: clicking opens it in a new tab instead of the entity page. */
  href?: string
  /** Clamp the description to 2 lines (default true). */
  clampDescription?: boolean
  /** Resolve the tone of a tag badge (defaults to the label hash). */
  tagToneOf?: (tag: string) => CategoryStyle
}

export default function EntityCard({
  entity,
  kinds,
  preview = false,
  href,
  clampDescription = true,
  tagToneOf = toneOfLabel,
}: EntityCardProps) {
  const navigate = useNavigate()
  const kind = kinds.find((k) => k.id === entity.kind_id)
  const tags: Array<string> = entity.tags ?? []

  const open = () => {
    if (preview) return
    if (href) window.open(href, '_blank', 'noopener')
    else
      navigate({
        to: '/communautes/$slug',
        params: { slug: entitySlug(entity.id, entity.name) },
      })
  }

  return (
    <Card
      padding={0}
      hoverable={!preview}
      onClick={open}
      style={{
        display: 'flex',
        flexDirection: 'column',
        cursor: preview ? 'default' : 'pointer',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '20px 20px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          flex: 1,
        }}
      >
        {/* Logo tile + type badge + name */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div
            style={{
              flexShrink: 0,
              width: 60,
              height: 60,
              borderRadius: 14,
              background: entity.image_uri ? '#fff' : 'var(--emerald-50)',
              border: `1px solid ${entity.image_uri ? 'var(--border-soft)' : 'var(--emerald-100)'}`,
              boxShadow: 'var(--shadow-xs)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: entity.image_uri ? 7 : 0,
              overflow: 'hidden',
            }}
          >
            {entity.image_uri ? (
              <img
                src={entity.image_uri}
                alt={entity.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <span style={{ fontSize: 28, lineHeight: 1 }}>{kind?.emoji}</span>
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            {kind && (
              <div style={{ marginBottom: 5 }}>
                <Badge tone="gold">
                  {kind.emoji} {kind.name}
                </Badge>
              </div>
            )}
            <h3
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontSize: 21,
                lineHeight: 1.15,
                color: 'var(--ink-0)',
              }}
            >
              {entity.name}
            </h3>
          </div>
        </div>

        {entity.location_text && (
          <Row
            gap="xs"
            style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.4 }}
          >
            <MapPin
              size={15}
              color="var(--gold-600)"
              style={{ flexShrink: 0, marginTop: 1 }}
            />
            {entity.location_text}
          </Row>
        )}

        {entity.description && (
          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              color: 'var(--ink-2)',
              lineHeight: 1.5,
              ...(clampDescription && {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
            }}
          >
            {stripMarkdown(entity.description)}
          </p>
        )}

        {/* Footer */}
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            borderTop: '1px solid var(--border-soft)',
          }}
        >
          <Row gap="xs" wrap>
            {tags.map((tag) => (
              <Badge key={tag} tone={tagToneOf(tag).tone}>
                {tag}
              </Badge>
            ))}
          </Row>
          {!preview && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: 'var(--emerald-700)',
                fontWeight: 600,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {href ? 'Visiter ↗' : 'Voir la page ↗'}
            </span>
          )}
        </div>
      </div>
    </Card>
  )
}
